import { useCallback, useEffect, useRef, useState } from 'react';
import {
  makePreparationAnswer,
  savePreparationDraft,
} from './preparationDraftService';
import {
  JOURNEY_ANSWER_ORIGIN,
  JOURNEY_ANSWER_STATUS,
  upsertJourneyAnswer,
} from './journeyAnswerModel';

const useJourneyAnswers = ({ childId, draftIdentity, savedAnswers = [], onSaved }) => {
  const [answers, setAnswers] = useState(savedAnswers);
  const [savingQuestionId, setSavingQuestionId] = useState('');
  const [error, setError] = useState('');
  const answersRef = useRef(savedAnswers);
  const savingRef = useRef(false);

  useEffect(() => {
    answersRef.current = savedAnswers;
    setAnswers(savedAnswers);
  }, [savedAnswers]);

  const saveAnswer = useCallback(async ({
    questionId, proposed = null, confirmedText = '', status, origin, sourceIds = [],
  }) => {
    if (savingRef.current) return false;
    const nextAnswer = makePreparationAnswer({
      questionId,
      proposedText: proposed?.proposedText || '',
      candidatePoints: proposed?.candidatePoints || [],
      confirmedText,
      status,
      origin,
      sourceIds,
      evidenceSourceIds: proposed?.evidenceSourceIds || proposed?.sourceIds || [],
      evidenceFingerprint: proposed?.evidenceFingerprint || null,
      generatedAt: proposed?.generatedAt || null,
    });
    const nextAnswers = upsertJourneyAnswer(answersRef.current, nextAnswer);
    savingRef.current = true;
    setSavingQuestionId(questionId);
    setError('');
    try {
      await savePreparationDraft({ childId, ...draftIdentity, answers: nextAnswers });
      answersRef.current = nextAnswers;
      setAnswers(nextAnswers);
      onSaved?.(nextAnswers);
      return true;
    } catch (saveError) {
      setError(saveError?.message || 'This answer could not be saved.');
      return false;
    } finally {
      savingRef.current = false;
      setSavingQuestionId('');
    }
  }, [childId, draftIdentity, onSaved]);

  return {
    answers,
    error,
    savingQuestionId,
    confirmSuggested: ({ questionId, proposed, text, sourceIds }) => saveAnswer({
      questionId, proposed, confirmedText: text,
      status: JOURNEY_ANSWER_STATUS.CONFIRMED,
      origin: JOURNEY_ANSWER_ORIGIN.AI_SUGGESTED,
      sourceIds,
    }),
    saveCaregiverAnswer: ({ questionId, proposed, text }) => saveAnswer({
      questionId, proposed, confirmedText: text,
      status: JOURNEY_ANSWER_STATUS.CONFIRMED,
      origin: JOURNEY_ANSWER_ORIGIN.CAREGIVER_AUTHORED,
      sourceIds: [],
    }),
    skipAnswer: ({ questionId, proposed }) => saveAnswer({
      questionId, proposed, confirmedText: '',
      status: JOURNEY_ANSWER_STATUS.SKIPPED,
      origin: proposed ? JOURNEY_ANSWER_ORIGIN.AI_SUGGESTED : JOURNEY_ANSWER_ORIGIN.CAREGIVER_AUTHORED,
      sourceIds: [],
    }),
    restoreAnswer: ({ questionId, proposed }) => saveAnswer({
      questionId, proposed, confirmedText: '',
      status: JOURNEY_ANSWER_STATUS.UNCONFIRMED,
      origin: proposed ? JOURNEY_ANSWER_ORIGIN.AI_SUGGESTED : JOURNEY_ANSWER_ORIGIN.CAREGIVER_AUTHORED,
      sourceIds: proposed?.sourceIds || [],
    }),
  };
};

export default useJourneyAnswers;
